import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/index.js';
import Header from '../../components/Header/Header.jsx';
import s from './Feeds.module.scss';

const SAMPLE_PRODUCT = 'https://rus-horeca.ru/upload/iblock/15f/m2vewlin4azisq6jq2hjl5ikcsuaqmvc.JPG';

const emptyRule = () => ({ label: '', brands: [], source: 'purchase', multiplier: 1 });

const FeedEditor = () => {
  const { feedId } = useParams();
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [cityName, setCityName] = useState('');
  const [drag, setDrag] = useState(null);
  const [frame, setFrame] = useState({ w: 1200, h: 900 });
  const [previewUrl, setPreviewUrl] = useState('');
  const [idCheck, setIdCheck] = useState(null);

  const load = async () => {
    const data = await api.feeds.getOne(feedId);
    if (data.error) {
      setError(data.error);
      return;
    }
    setFeed(data);
  };

  useEffect(() => {
    load();
  }, [feedId]);

  useEffect(() => {
    if (!feed?.image_layout?.template_name) return undefined;
    let cancelled = false;
    let objectUrl = '';
    (async () => {
      const blob = await api.feeds.templateFile(feedId);
      if (cancelled || !blob || blob.error) return;
      objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [feedId, feed?.image_layout?.template_name]);

  if (!feed) {
    return <div className={s.wrap}>{error || 'Загрузка'}</div>;
  }

  const layout = feed.image_layout || {};
  const photo = layout.photo || { x: 0, y: 0, w: 200, h: 200 };
  const text = layout.text || { x: 0, y: 0, w: 200, h: 80 };

  const patch = (part) => setFeed({ ...feed, ...part });

  const save = async () => {
    setError('');
    const data = await api.feeds.save(feedId, {
      bitrix_url: feed.bitrix_url,
      current_s3_key: feed.current_s3_key,
      description: feed.description || '',
      equal_purchase_rrp: feed.equal_purchase_rrp,
      city_ids: (feed.cities || []).filter((city) => city.checked).map((city) => city._id),
      city_meta: Object.fromEntries((feed.cities || []).map((city) => [city._id, { key: city.key || '', address: city.address || '' }])),
      price_rules: feed.price_rules,
      image_layout: feed.image_layout,
    });
    if (data.error) {
      setError(data.error);
      return;
    }
    setFeed(data);
    setStatus('Сохранено. Прошлая конфигурация лежит в версиях.');
  };

  const addCity = async () => {
    const data = await api.feeds.addCity({ name: cityName });
    if (data.error) {
      setError(data.error);
      return;
    }
    setCityName('');
    const exists = (feed.cities || []).some((city) => city._id === data._id);
    const cities = exists
      ? feed.cities
      : [...feed.cities, { ...data, checked: false }];
    setFeed({ ...feed, cities });
    setStatus(exists ? 'Такой город уже есть в общем списке' : 'Город добавлен в общий список');
  };

  const watchJob = async (starter) => {
    setError('');
    setStatus('Запуск');
    const started = await starter(feedId);
    if (started.error) {
      setError(started.error);
      return;
    }
    const tick = async () => {
      const current = await api.feeds.job(started._id);
      if (current.error) {
        setError(current.error);
        return;
      }
      if (current.status === 'running') {
        setTimeout(tick, 3000);
        return;
      }
      setStatus(current.message || current.status);
      if (current.status === 'failed') {
        setError(current.message);
      }
      load();
    };
    tick();
  };

  const checkIds = async () => {
    setError('');
    setIdCheck(null);
    setStatus('Сверяю Id со старым фидом');
    const cities = (feed.cities || []).filter((city) => city.checked).map((city) => ({
      key: city.key,
      name: city.name,
      address: city.address,
      checked: true,
    }));
    const started = await api.feeds.checkIds(feedId, cities);
    if (started.error) {
      setError(started.error);
      return;
    }
    const tick = async () => {
      const current = await api.feeds.job(started._id);
      if (current.error) {
        setError(current.error);
        return;
      }
      if (current.status === 'running') {
        setTimeout(tick, 3000);
        return;
      }
      setIdCheck(current);
      setStatus('');
      if (current.status === 'failed') {
        setError(current.message);
      }
    };
    tick();
  };

  const onTemplate = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
    setStatus('Загрузка шаблона');
    const data = await api.feeds.uploadTemplate(feedId, file);
    if (data.error) {
      setError(data.error);
      setStatus('');
      return;
    }
    setFeed(data);
    setStatus('Шаблон загружен. Ниже можно менять размер текста и рамок.');
  };

  const setLayoutBox = (name, key, value) => {
    const current = name === 'photo' ? photo : text;
    patch({
      image_layout: {
        ...layout,
        [name]: { ...current, [key]: Math.max(20, Number(value) || 0) },
      },
    });
  };

  const onBoxDown = (name, event, mode) => {
    const rect = stageRef.current.getBoundingClientRect();
    const scale = rect.width / frame.w;
    const box = name === 'photo' ? photo : text;
    setDrag({
      name,
      mode,
      dx: (event.clientX - rect.left) / scale - box.x,
      dy: (event.clientY - rect.top) / scale - box.y,
    });
  };

  const onMove = (event) => {
    if (!drag || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const scale = rect.width / frame.w;
    const pointerX = (event.clientX - rect.left) / scale;
    const pointerY = (event.clientY - rect.top) / scale;
    const current = drag.name === 'photo' ? photo : text;
    const next = drag.mode === 'resize'
      ? {
        ...current,
        w: Math.max(40, Math.round(pointerX - current.x)),
        h: Math.max(40, Math.round(pointerY - current.y)),
      }
      : {
        ...current,
        x: Math.round(pointerX - drag.dx),
        y: Math.round(pointerY - drag.dy),
      };
    patch({
      image_layout: { ...layout, [drag.name]: next },
    });
  };

  const boxStyle = (box) => ({
    left: `${(box.x / frame.w) * 100}%`,
    top: `${(box.y / frame.h) * 100}%`,
    width: `${(box.w / frame.w) * 100}%`,
    height: `${(box.h / frame.h) * 100}%`,
  });

  return (
    <div onMouseMove={onMove} onMouseUp={() => setDrag(null)}>
      <Header
        title={feed.title}
        breadCrumbs={[
          { name: 'Главная', link: '/' },
          { name: 'Фиды', link: '/feeds' },
          { name: feed.title, link: `/feeds/${feedId}` },
        ]}
      />
      <div className={s.wrap}>
        {error && <div className={s.error}>{error}</div>}
        {status && <div className={s.ok}>{status}</div>}

        <section className={s.group}>
          <div className={s.groupHeader}><h3 className={s.groupTitle}>Источник</h3></div>
          <div className={s.groupBody}>
            <label className={s.field}>
              <span className={s.label}>Выгрузка Битрикса</span>
              <input className={s.input} value={feed.bitrix_url || ''} onChange={(e) => patch({ bitrix_url: e.target.value })} />
            </label>
            <div className={s.field}>
              <span className={s.label}>Последний файл</span>
              {feed.current_url ? (
                <div className={s.linkRow}>
                  <a className={s.fullLink} href={feed.current_url} target="_blank" rel="noreferrer">{feed.current_url}</a>
                  <button
                    className="btn grey"
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(feed.current_url);
                      setStatus('Ссылка скопирована');
                    }}
                  >
                    Копировать ссылку
                  </button>
                </div>
              ) : (
                <p className={s.hint}>Файла ещё нет. Он появится после обновления цен или полной перегенерации.</p>
              )}
            </div>
          </div>
        </section>

        <section className={s.group}>
          <div className={s.groupHeader}><h3 className={s.groupTitle}>Города</h3></div>
          <div className={s.groupBody}>
            <p className={s.hint}>Галочка включает город в этот фид. Ключ — хвост Id, он свой у каждого фида. Для Екатеринбурга Ресторан Мастера это rvxbzxcdre, целиком Id выглядит как 245504rvxbzxcdre.</p>
            <div className={s.cities}>
              {(feed.cities || []).map((city) => (
                <div key={city._id} className={s.city}>
                  <label className={s.cityCheck}>
                    <input
                      type="checkbox"
                      checked={!!city.checked}
                      onChange={(e) => patch({
                        cities: feed.cities.map((item) => item._id === city._id ? { ...item, checked: e.target.checked } : item),
                      })}
                    />
                    <span>
                      <span className={s.cityName}>{city.name}</span>
                      <span className={s.cityAddress}>{city.address}</span>
                    </span>
                  </label>
                  <label className={s.keyField}>
                    <span>Ключ в Id</span>
                    <input
                      className={s.input}
                      value={city.key || ''}
                      onChange={(e) => patch({
                        cities: feed.cities.map((item) => item._id === city._id ? { ...item, key: e.target.value } : item),
                      })}
                    />
                  </label>
                  <span className={s.hint}>245504{city.key || ''}</span>
                </div>
              ))}
            </div>
            <div className={s.row}>
              <input className={s.input} style={{ maxWidth: 280 }} placeholder="Новый город" value={cityName} onChange={(e) => setCityName(e.target.value)} />
              <button className="btn grey" onClick={addCity}>Добавить</button>
            </div>
          </div>
        </section>

        <section className={s.group}>
          <div className={s.groupHeader}><h3 className={s.groupTitle}>Формулы цен</h3></div>
          <div className={s.groupBody}>
            <p className={s.hint}>Если закуп и РРЦ совпали, отдельного закупа нет — в фид ставится РРЦ без наценки. Множитель 0.8 это −20%, 1.05 это +5%.</p>
            <div className={s.rulesHead}>
              <span>Название</span><span>Бренды через запятую</span><span>Откуда</span><span>Множитель</span><span />
            </div>
            {(feed.price_rules || []).map((rule, index) => (
              <div className={s.rule} key={index}>
                <input className={s.input} value={rule.label || ''} onChange={(e) => {
                  const price_rules = [...feed.price_rules];
                  price_rules[index] = { ...rule, label: e.target.value };
                  patch({ price_rules });
                }} />
                <input
                  className={s.input}
                  value={(rule.brands || []).join(', ')}
                  onChange={(e) => {
                    const price_rules = [...feed.price_rules];
                    price_rules[index] = { ...rule, brands: e.target.value.split(',').map((item) => item.trim()).filter(Boolean) };
                    patch({ price_rules });
                  }}
                />
                <select className={s.select} value={rule.source} onChange={(e) => {
                  const price_rules = [...feed.price_rules];
                  price_rules[index] = { ...rule, source: e.target.value };
                  patch({ price_rules });
                }}>
                  <option value="purchase">закуп</option>
                  <option value="rrp">РРЦ</option>
                </select>
                <input className={s.input} type="number" step="0.01" value={rule.multiplier} onChange={(e) => {
                  const price_rules = [...feed.price_rules];
                  price_rules[index] = { ...rule, multiplier: Number(e.target.value) };
                  patch({ price_rules });
                }} />
                <button className={s.iconBtn} type="button" onClick={() => patch({ price_rules: feed.price_rules.filter((_, i) => i !== index) })}>×</button>
              </div>
            ))}
            <button className="btn grey" onClick={() => patch({ price_rules: [...(feed.price_rules || []), emptyRule()] })}>Добавить правило</button>
          </div>
        </section>

        <section className={s.group}>
          <div className={s.groupHeader}><h3 className={s.groupTitle}>Картинка</h3></div>
          <div className={s.groupBody}>
            <p className={s.hint}>Сплошная рамка — место фото товара, пунктир — место названия. Рамку двигают за край, размер меняют за угол или числами ниже. После правок нажмите «Сохранить».</p>
            <div className={s.row}>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                hidden
                onChange={onTemplate}
              />
              <button className="btn grey" type="button" onClick={() => fileRef.current && fileRef.current.click()}>
                Загрузить с компьютера
              </button>
              <span className={s.hint}>{layout.template_name || 'Файл ещё не выбран'}</span>
            </div>
            <div className={s.sizes}>
              <label className={s.sizeField}>
                <span>Размер текста, px</span>
                <input className={s.input} type="number" min="8" value={layout.font_size || 56} onChange={(e) => patch({ image_layout: { ...layout, font_size: Number(e.target.value) || 8 } })} />
              </label>
              <label className={s.sizeField}>
                <span>Ширина фото</span>
                <input className={s.input} type="number" min="40" value={photo.w} onChange={(e) => setLayoutBox('photo', 'w', e.target.value)} />
              </label>
              <label className={s.sizeField}>
                <span>Высота фото</span>
                <input className={s.input} type="number" min="40" value={photo.h} onChange={(e) => setLayoutBox('photo', 'h', e.target.value)} />
              </label>
              <label className={s.sizeField}>
                <span>Ширина названия</span>
                <input className={s.input} type="number" min="40" value={text.w} onChange={(e) => setLayoutBox('text', 'w', e.target.value)} />
              </label>
              <label className={s.sizeField}>
                <span>Высота названия</span>
                <input className={s.input} type="number" min="40" value={text.h} onChange={(e) => setLayoutBox('text', 'h', e.target.value)} />
              </label>
            </div>
            <div className={s.stageWrap} ref={stageRef}>
              {(previewUrl || layout.template_url) ? (
                <img
                  src={previewUrl || layout.template_url}
                  alt=""
                  onLoad={(e) => setFrame({ w: e.target.naturalWidth || 1200, h: e.target.naturalHeight || 900 })}
                />
              ) : (
                <div className={s.board} style={{ aspectRatio: `${frame.w} / ${frame.h}` }} />
              )}
              <div className={s.box} style={boxStyle(photo)} onMouseDown={(e) => onBoxDown('photo', e, 'move')}>
                <img className={s.samplePhoto} src={SAMPLE_PRODUCT} alt="" />
                <span className={s.handle} onMouseDown={(e) => { e.stopPropagation(); onBoxDown('photo', e, 'resize'); }} />
              </div>
              <div
                className={`${s.box} ${s.textBox}`}
                style={{ ...boxStyle(text), fontSize: `calc(${layout.font_size || 56} * 100cqw / ${frame.w})` }}
                onMouseDown={(e) => onBoxDown('text', e, 'move')}
              >
                Название товара
                <span className={s.handle} onMouseDown={(e) => { e.stopPropagation(); onBoxDown('text', e, 'resize'); }} />
              </div>
            </div>
          </div>
        </section>

        <div className={s.row}>
          <button className="btn dark" onClick={save}>Сохранить</button>
          <button className="btn grey" type="button" onClick={checkIds}>Проверить Id</button>
          <button className="btn grey" onClick={() => watchJob(api.feeds.refreshPrices)}>Обновить только цены</button>
          <button className="btn light" onClick={() => watchJob(api.feeds.regenerate)}>Полная перегенерация</button>
        </div>
        {idCheck && idCheck.mismatch_count > 0 && (
          <div className={s.error}>
            <strong>{idCheck.message}</strong>
            <ul className={s.warnList}>
              {(idCheck.mismatches || []).map((row) => (
                <li key={`${row.old_id}-${row.new_id}`}>
                  {row.city}: было {row.old_id}, стало {row.new_id}
                </li>
              ))}
            </ul>
            {idCheck.mismatch_count > (idCheck.mismatches || []).length && (
              <p>Показаны первые {idCheck.mismatches.length} из {idCheck.mismatch_count}.</p>
            )}
          </div>
        )}
        {idCheck && idCheck.ok && <div className={s.ok}>{idCheck.message}</div>}
        <p className={s.hint}>Проверка сравнивает последний файл с тем, который соберётся по текущим городам и ключам. Если Id товара в одном городе другой, Авито посчитает объявление дублем. Файл при проверке не загружается.</p>

        <section className={s.group}>
          <div className={s.groupHeader}><h3 className={s.groupTitle}>Версии</h3></div>
          <div className={`${s.groupBody} ${s.versions}`}>
            {(feed.versions || []).length === 0 && <p className={s.hint}>Пока пусто. Версия пишется при сохранении и после генерации.</p>}
            {(feed.versions || []).map((version) => (
              <div className={s.version} key={version._id}>
                <span className={s.versionKind}>{version.kind}</span>
                <span className={s.hint}>{version.created_at}{version.verification ? ` · ${version.verification}` : ''}</span>
                {version.url && <a className={s.feedLink} href={version.url}>Открыть файл</a>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default FeedEditor;
